import { Accordion, Button } from '@zoom/components';
import classnames from 'classnames';
import { equals } from 'ramda';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SchedulingStudioFilterSidebarDropdown } from './SchedulingStudioFilterSidebarDropdown';
import { ACTIVE_SIDEBAR_FILTERS_COUNTER, APPLY_FILTER_BUTTON_TAG } from './constants';
import {
  CheckboxFilter,
  CheckboxFilterAccordionType,
  CheckboxFilterTile,
  createCheckboxFilterTileSubtitle,
  queuesToCheckboxOptionsObj,
} from '../../components/CheckboxFilterTile/CheckboxFilterTile';
import { Sidebar, SidebarBody, SidebarHeader } from '../../components/layout/Sidebar';
import { QueuesResult } from '../../graphql/types';

export type SchedulingStudioSidebarFilterState = {
  queues: Record<string, CheckboxFilter>;
};

type SchedulingStudioFilterSidebarProps = {
  handleCancel: () => void;
  preOpenedItemIds: string[];
  queuesResult: QueuesResult;
  setPreOpenedItemIds: (itemIds: SchedulingStudioFilterSidebarProps['preOpenedItemIds']) => void;
  setSidebarFilter: (filterState: SchedulingStudioFilterSidebarProps['sidebarFilter']) => void;
  sidebarFilter: SchedulingStudioSidebarFilterState;
};

export const SchedulingStudioFilterSidebar: React.FC<SchedulingStudioFilterSidebarProps> = ({
  handleCancel,
  preOpenedItemIds,
  queuesResult,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
}) => {
  const { t } = useTranslation();

  // Filters states
  const [selectedQueues, setSelectedQueues] = useState<Record<string, CheckboxFilter>>(
    queuesToCheckboxOptionsObj(queuesResult, sidebarFilter.queues)
  );

  // Creating arrays[] of assigned Ids
  const assignedQueueIds = Object.keys(selectedQueues).filter((curr) => selectedQueues[curr].assigned);

  // Active filters Ids arrays[]
  const activeQueueIds = Object.keys(sidebarFilter.queues);

  const handleApply = () => {
    const checkedQueues: Record<string, CheckboxFilter> = {};

    assignedQueueIds.forEach((assignedQueueId) => {
      checkedQueues[assignedQueueId] = selectedQueues[assignedQueueId];
    });

    setSidebarFilter({
      queues: checkedQueues,
    });

    handleCancel();
  };

  const handleResetAllFilters = () => {
    setSelectedQueues(queuesToCheckboxOptionsObj(queuesResult, sidebarFilter.queues, true));

    setPreOpenedItemIds([]);
  };

  const countOfActiveFilters = [!!activeQueueIds.length].filter((active) => active).length;

  const isFilterActive = assignedQueueIds.length > 0;

  const isApplyActive = equals(assignedQueueIds, activeQueueIds);

  // Accordion filter options
  const filters: CheckboxFilterAccordionType[] = [
    {
      className: activeQueueIds.length ? 'filter--active' : '',
      dataTestIdPrefix: 'scheduling-sidebar-filter-queues',
      id: '1',
      items: selectedQueues,
      name: t('queue.queues'),
      setItems: setSelectedQueues,
      subtitle: createCheckboxFilterTileSubtitle(t, selectedQueues, assignedQueueIds),
    },
  ];

  return (
    <Sidebar className="sidebar-filter">
      <SidebarHeader onClose={handleCancel}>
        {/* eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components */}
        <Button data-test-id={APPLY_FILTER_BUTTON_TAG} disabled={isApplyActive} kind="secondary" onClick={handleApply}>
          {t('form.apply')}
        </Button>
        <p
          className={classnames('sidebar-filter__header-wrapper mr-auto', {
            'sidebar-filter__header-wrapper--inactive': !countOfActiveFilters,
          })}
          data-test-id={ACTIVE_SIDEBAR_FILTERS_COUNTER}
        >
          <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
            {/* space needs to be added within strong tag so the tests can run correctly */}
            {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
            <strong>{{ countOfActiveFilters }} </strong>Active filters
          </Trans>
        </p>
        <SchedulingStudioFilterSidebarDropdown
          isFilterActive={isFilterActive}
          resetAllFilters={handleResetAllFilters}
          t={t}
        />
      </SidebarHeader>
      <SidebarBody>
        <Accordion
          items={filters}
          label={({ name }: CheckboxFilterAccordionType) => name}
          preOpenedItemIds={preOpenedItemIds}
        >
          {(item) => <CheckboxFilterTile filter={item} />}
        </Accordion>
      </SidebarBody>
    </Sidebar>
  );
};
